import { Injectable } from '@angular/core';
import { get } from 'lodash';
import { isToday } from 'date-fns';

import { Orders, Order } from './_interfaces_/orders.interface';
import { OrderMapped } from './_interfaces_/order.mapped.interface';
import { OrderStatusEnum } from '@enums/order-status.enum';
import {
  BookedOrder,
  BookedOrderPassenger,
  BookedOrderBookingInfo,
  BookedOrderBaggage,
  BookedOrderActionButton,
  BookedOrderFlightBooking,
  BookedOrderFlightSegment,
  BookedOrderFlightBookingActionInfo,
} from './_interfaces_/booked-order.interface';
import {
  BookedOrderMapped,
  BookedOrderPnrMapped,
  BookedOrderPassengerMapped,
  BookedOrderSegmentMapped,
  BookedOrderConditionMapped,
  LinksMapped,
  BookedOrderBaggageMapped,
  BookedOrderActionInfoMapped,
} from './_interfaces_/booked-order.mapped.interface';
import { TicketConditions, TicketConditionsTitle } from './_interfaces_/ticket-conditions.interface';
import { TicketConditionsTitleMapped, TicketConditionsMapped } from './_interfaces_/ticket-conditions.mapped.interface';
import { BookedOrderLinkEnum } from '@enums/booked-order-link.enum';

@Injectable({
  providedIn: 'root',
})
export class OrderMapperService {
  constructor() {}

  public mapOrders(ordersObject: Orders): OrderMapped[] {
    const orders: Order[] = ordersObject.orders;
    const hasOrders: boolean = orders.length > 0;
    const mappedOrders: OrderMapped[] = hasOrders
      ? orders.map((order) => ({
          departureDate: order.departureDate,
          destination: order.endDestination,
          mainPassengerName: order.mainPassengerName,
          orderNumber: order.orderNumber,
          orderStatus: order.orderStatus,
          pnr: order.pnr,
          id: order.id,
          dossierNumber: order.dossierNumber,
          expiryDate: order.expiryDate,
        }))
      : [];
    const validOrders: OrderMapped[] = mappedOrders.filter(order => this.validateOrder(order));
    return validOrders;
  }

  public mapBookedOrder(dossier: BookedOrder): BookedOrderMapped {
    return {
      dossierNumber: dossier.orderId,
      pnrs: dossier.flightBookings.map((flightBooking) => this.mapBookedOrderPnr(flightBooking)),
      links: this.mapLinks(dossier.actionButtonInfo),
    };
  }

  public mapLinks(linksInfo: BookedOrderActionButton): Array<LinksMapped> {
    const links: LinksMapped[] = [];
    const createLink = (name, url, method) =>
      links.push({
        name: name,
        url: url,
        method: method,
      });
    if (linksInfo.sendInvoicePossible)
      createLink(BookedOrderLinkEnum.SendInvoice, linksInfo.sendInvoiceLink, linksInfo.sendInvoiceLinkMethod);
    if (linksInfo.sendTicketsPossible)
      createLink(BookedOrderLinkEnum.SendEticket, linksInfo.sendTicketsLink, linksInfo.sendTicketsLinkMethod);
    if (linksInfo.ticketConditionsRetrievable)
      createLink(BookedOrderLinkEnum.TicketConditions, linksInfo.ticketConditionsLink, linksInfo.ticketConditionsLinkMethod);

    return links;
  }

  public mapBookedOrderPnr(flightBooking: BookedOrderFlightBooking): BookedOrderPnrMapped {
    const segments = flightBooking.flightLegs.flatMap((flightLeg) =>
      flightLeg.flightSegments.map((segment) => {
        return segment;
      })
    );
    return {
      pnr: flightBooking.bookingReference,
      orderId: flightBooking.orderReference,
      passengers: flightBooking.passengers.map((passenger) => this.mapBookedOrderPassenger(passenger, segments)),
      segments: this.mapBookedOrderSegments(segments),
      bundleFare: flightBooking.bundledFare,
      actionInfo: this.mapActionInfo(flightBooking.flightBookingActionInfo),
      officeId: flightBooking.officeId,
      providerSupplierReference: flightBooking.providerSupplierReference,
      validatingAirline: flightBooking.validatingAirline,
    };
  }

  public mapActionInfo(actionInfo: BookedOrderFlightBookingActionInfo): BookedOrderActionInfoMapped {
    const mappedActionInfo: BookedOrderActionInfoMapped = { ...actionInfo };
    return mappedActionInfo;
  }

  public mapBookedOrderPassenger(passenger: BookedOrderPassenger, segments: Array<BookedOrderFlightSegment>): BookedOrderPassengerMapped {
    return {
      dateOfBirth: passenger.dateOfBirth,
      displayName: `${passenger.firstName} ${passenger.lastName}`,
      extrasInfo: passenger.extrasInfo,
      gender: passenger.gender,
    };
  }

  public mapBookedOrderBaggage(baggageArray: BookedOrderBaggage[]): BookedOrderBaggageMapped[] {
    return baggageArray.map((baggage: BookedOrderBaggage) => ({
      paidIndicator: baggage.paidIndicator,
      segmentId: baggage.segmentId,
      baggageAllowance: baggage.baggageAllowance,
    }));
  }

  public mapBookedOrderConditions(
    passengerBookingDetails: BookedOrderBookingInfo,
    segments: Array<BookedOrderFlightSegment>
  ): BookedOrderConditionMapped[] {
    const conditions: BookedOrderConditionMapped[] = [];

    segments.forEach((segment, index) => {
      conditions[index] = {
        segmentId: index,
        baggage: passengerBookingDetails.baggageCode,
        seatNumber: passengerBookingDetails.seat,
        preferences: passengerBookingDetails.preferences,
        confirmed: passengerBookingDetails.confirmed,
        departureAirport: segment.departureAirportCode,
        arrivalAirport: segment.arrivalAirportCode,
      };
    });

    return conditions;
  }

  public mapBookedOrderSegments(segments: Array<BookedOrderFlightSegment>): BookedOrderSegmentMapped[] {
    const mappedSegments: BookedOrderSegmentMapped[] = [];
    segments.forEach((segment, index) => {
      let newMappedSegment: BookedOrderSegmentMapped = {
        arrivalAirportIataCode: segment.arrivalAirportCode,
        stopoverIndicator: segment.stopoverIndicator,
        checkInTerminal: segment.departureAirportName,
        segmentId: index,
        airlineDescription: segment.airlineName,
        codeShareDescription: segment.codeShareDescription,
        arrivalDescription: segment.arrivalAirportName,
        bundledAirtradeFareCode: segment.bundledFareName,
        cabinClass: segment.cabinClass,
        arrivalDate: segment.arrivalDate,
        arrivalTime: segment.arrivalTime,
        flightNumber: segment.flightNumber,
        departureAirportIataCode: segment.departureAirportCode,
        departureDescription: segment.departureAirportName,
        departureDate: segment.departureDate,
        departureTime: segment.departureTime,
        airlineIataCode: segment.airlineCode,
      };

      mappedSegments.push(newMappedSegment);
    });
    return mappedSegments;
  }

  public mapTicketConditions(ticketConditions: TicketConditions): TicketConditionsMapped {
    const { ErrorMessage: error } = ticketConditions;
    const { Headers: headers } = ticketConditions.DossierDetails;
    return {
      error,
      conditions: headers
        ? headers.map((header) => ({
            id: header.Header.replace(/\s/g, ''),
            header: header.Header,
            titles: this.mapTicketTitles(header.Titles),
          }))
        : [],
    };
  }

  public mapTicketTitles(titles: TicketConditionsTitle[]): TicketConditionsTitleMapped[] {
    return titles.map((title) => ({
      title: title.Title,
      texts: title.TextLines.map((text) => text.Text),
    }));
  }

  private validateOrder(order: OrderMapped): OrderMapped {
    switch (order.orderStatus) {
      case OrderStatusEnum.BOOKED:
        if (
          order.dossierNumber && order.dossierNumber.length !== 0
          && order.orderNumber && order.orderNumber.length !== 0
          && order.pnr && order.pnr.length !== 0
        ) {
          return order;
        }
        break;
      case OrderStatusEnum.OPTION:
        if (
          order.id && order.id.length !== 0
          && order.orderNumber && order.orderNumber.length !== 0
          && order.pnr && order.pnr.length !== 0
        ) {
          return order;
        }
        break;
    }
  }
}
